import { useEffect } from "react";

type SeoProps = {
  title?: string; // page-specific title; site name is appended automatically
  description?: string;
  image?: string; // absolute URL
  url?: string;
  type?: string; // og:type, e.g. "website" | "article"
};

const SITE = "Brown Esports";
const DEFAULT_DESC =
  "Brown Esports is Brown University's home for both casual and competitive gaming.";
const DEFAULT_IMAGE = "https://brownesports.org/icon-512x512.png";

function setMeta(attr: "name" | "property", key: string, content?: string) {
  if (!content) return;
  let el = document.head.querySelector(
    `meta[${attr}="${key}"]`
  ) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

/**
 * Imperatively set document title + SEO/Open Graph meta tags. Used instead of
 * react-helmet (which is unreliable on React 19). Note: this runs client-side,
 * so JS-rendering crawlers (Google) and browsers get per-page tags; non-JS
 * social crawlers (Discord/Twitter/Facebook) read the static index.html tags.
 */
export function useSeo(props: SeoProps) {
  const fullTitle = props.title ? `${props.title} | ${SITE}` : SITE;
  const description = props.description || DEFAULT_DESC;
  const hasImage = !!props.image; // a real (landscape) photo vs the square logo fallback
  const image = props.image || DEFAULT_IMAGE;
  const type = props.type || "website";
  const twitterCard = hasImage ? "summary_large_image" : "summary";
  useEffect(() => {
    document.title = fullTitle;
    setMeta("name", "description", description);
    setMeta("property", "og:title", props.title || SITE);
    setMeta("property", "og:description", description);
    setMeta("property", "og:type", type);
    setMeta("property", "og:image", image);
    setMeta("property", "og:url", props.url || window.location.href);
    setMeta("property", "og:site_name", SITE);
    setMeta("name", "twitter:card", twitterCard);
    setMeta("name", "twitter:title", props.title || SITE);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", image);
  }, [fullTitle, description, image, type, twitterCard, props.title, props.url]);
}
